import { hiddenText, isPersonalUrl, isPersonalUrlKey, setKnownMask } from '../../shared/redact';
import { notPeopleUrl } from './config';

/*
 * Dictionary of known names and message texts: what the app itself received from personal API paths (and quotes from any
 * path) is hidden wherever it shows up — HTML, attributes, headings, console, clicks. A quote is a piece of a known
 * message: pieces from MIN_FRAGMENT chars in a row (or MIN_QUOTED in guillemets) are hidden too; a name only as a whole
 * word, case-sensitive and not shorter than MIN_NAME, so identifiers like "tab=chat" or "must_not" stay intact.
 *
 * Quint people_data Мс8 (report 190): everything the app received about a person is known — names, nicknames, Telegram
 * IDs and phones (`numbers`, digits only), notes, titles of personal chats — and the dictionary never drops a person for
 * its size (it had 400 names for 2453 members). A name is looked up word by word, not by one huge alternation.
 */
const MIN_NAME = 4;
const MIN_DIGITS = 5;
const MAX_DIGITS = 18;
const MIN_TEXT = 12;
const MAX_TEXT = 600;
const MIN_FRAGMENT = 16;
const MIN_QUOTED = 8;

const NAME_KEYS = new Set([
  'displayname', 'firstname', 'lastname', 'middlename', 'fullname', 'tgusername', 'username', 'nickname', 'nick',
  'candidatename', 'contactname', 'ownername', 'authorname', 'sendername', 'peername',
]);
const LOOSE_NAME_KEYS = new Set(['name', 'chattitle', 'dialogtitle', 'conversationtitle', 'peertitle', 'chatname', 'contacttitle']);
// Any key ending in «name» (telegramName, ownName, fwdFromName) holds a person on a path of people; any ending in «username» or
// «nick» (memberUsername, inviterUsername) holds a nickname wherever the path is personal. Not a person: a file, a type, a role…
const PERSON_NAME_SUFFIX = /name$/;
const NICK_SUFFIX = /(?:username|nickname|nick)$/;
const NOT_PERSON_NAME = /^(?:file|type|class|tag|host|role|stage|status|location|country|region|kind|label|column|field|model|process|service|account|project|section|short|display_?)name$/;
// A person's number: Telegram ID of a user or a private chat, phone. Any other long number of a personal path counts too
// (`learnPair`), unless it is a time, a size or a count.
const NUMBER_KEYS = new Set([
  'telegramid', 'tgid', 'telegramuserid', 'tguserid', 'telegramchatid', 'tgchatid', 'chatid', 'peerid', 'senderid', 'fromid',
  'phone', 'phonenumber', 'tel', 'mobile', 'contactphone',
]);
const NOT_NUMBER_KEY = /(?:created|updated|deleted|sent|read|last|joined|seen|expires|started|finished|checked)(?:at|on)?$|(?:time|date|ms|ts|size|bytes|count|total|limit|offset|version|timeout|seconds|duration)$/;
// Notes about a person: what the staff wrote down.
const NOTE_KEYS = new Set(['note', 'notes', 'comment', 'comments', 'bio', 'about', 'memo', 'remark', 'remarks']);
// Words of a conversation: long ones are learnt as pieces of a message.
const BODY_KEYS = new Set(['text', 'message', 'body', 'content']);
// Which personal paths carry titles in `name` (tasks, accounts…) is host knowledge: `configure({ notPeopleUrl })`.

const QUOTE_KEYS = new Set(['quote', 'quotes', 'evidence', 'contextbefore', 'contextafter', 'snippet', 'excerpt', 'replyquote', 'filename']);

const names = new Set<string>();
const texts = new Set<string>();
/** Digits of Telegram IDs and phones (and the last ten digits of a long phone: the same number written differently). */
const numbers = new Set<string>();
/** Names of several words ("Ян Ли"): by their first word, the longest first. */
const phrases = new Map<string, string[]>();
let starts: Set<string> | null = null;
let joined = '';

let version = 0;

/** Grows with every name or text learned: what was cleaned with an older dictionary is cleaned again (П217). */
export const knownVersion = (): number => version;

const listeners = new Set<() => void>();
let notifyQueued = false;

/** Called once per batch of learned words (a microtask after them — still before the next paint): screen mode, П600. */
export function onKnownChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function changed(): void {
  version += 1;
  if (notifyQueued || listeners.size === 0) return;
  notifyQueued = true;
  queueMicrotask(() => {
    notifyQueued = false;
    for (const fn of listeners) fn();
  });
}

function add(set: Set<string>, value: string): void {
  if (set.has(value)) return;
  changed();
  set.add(value);
  starts = null;
}

const WORD = /[\p{L}\p{N}_]+(?:['’.-][\p{L}\p{N}_]+)*/gu;

function learnName(raw: string): void {
  const value = raw.trim();
  if (value.length < MIN_NAME || /^[\d\s.+-]+$/.test(value)) return;
  add(names, value);
  const parts = value.split(/[\s,·|/()]+/).filter(Boolean);
  for (const part of parts) if (part.length >= MIN_NAME && !/^[\d.+-]+$/.test(part)) add(names, part);
  // A name of several words is looked for as a whole too: its parts may be shorter than MIN_NAME ("Ян Ли").
  const head = value.match(WORD)?.[0];
  if (head && /\s/.test(value)) {
    const list = phrases.get(head) ?? [];
    if (!list.includes(value)) {
      list.push(value);
      list.sort((a, b) => b.length - a.length);
      phrases.set(head, list);
    }
  }
}

function learnText(raw: string): void {
  const value = raw.trim();
  if (value.length >= MIN_TEXT && value.length <= MAX_TEXT) add(texts, value);
}

function learnNumber(raw: string): void {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < MIN_DIGITS || digits.length > MAX_DIGITS) return;
  add(numbers, digits);
  if (digits.length > 10) add(numbers, digits.slice(-10));
}

/** A note is short at times ("VIP", "звонить вечером"): long ones are pieces of text, short ones are words. */
function learnNote(raw: string): void {
  if (raw.trim().length >= MIN_TEXT) learnText(raw);
  else learnName(raw);
}

interface Scope {
  personal: boolean;
  people: boolean;
}

// A lowercase identifier in `name` ("personal", "work") is an account or a kind, not a person.
function learnPair(key: string, value: string, scope: Scope): void {
  const norm = key.toLowerCase().replace(/[-_]/g, '');
  if (QUOTE_KEYS.has(norm)) learnText(value);
  else if (!scope.personal) return;
  else if (NAME_KEYS.has(norm)) learnName(value);
  else if (NUMBER_KEYS.has(norm)) learnNumber(value);
  else if (NOTE_KEYS.has(norm)) learnNote(value);
  else if (BODY_KEYS.has(norm)) learnText(value);
  else if (/^\+?[\d\s()-]{7,}$/.test(value.trim()) && !NOT_NUMBER_KEY.test(norm)) learnNumber(value);
  else if (NICK_SUFFIX.test(norm)) learnName(value);
  else if (LOOSE_NAME_KEYS.has(norm) || (scope.people && PERSON_NAME_SUFFIX.test(norm) && !NOT_PERSON_NAME.test(norm))) {
    if (scope.people && !/^[a-z0-9_.-]+$/.test(value.trim())) learnName(value);
  }
  else if (isPersonalUrlKey(key)) learnText(value);
}

function walk(value: unknown, depth: number, scope: Scope): void {
  if (depth > 10 || !value || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    for (const item of value) walk(item, depth + 1, scope);
    return;
  }
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'string') learnPair(key, item, scope);
    else if (typeof item === 'number' && Number.isInteger(item)) learnPair(key, String(item), scope);
    else if (Array.isArray(item) && QUOTE_KEYS.has(key.toLowerCase())) {
      for (const one of item) if (typeof one === 'string') learnText(one);
    } else walk(item, depth + 1, scope);
  }
}

export function learnFrom(url: string, text: string): void {
  if (!text) return;
  const notPeople = notPeopleUrl();
  const scope: Scope = { personal: isPersonalUrl(url), people: !notPeople || !notPeople.test(url) };
  if (!scope.personal && !/"(?:quotes?|evidence|context_?before|context_?after|snippet|excerpt|reply_?quote|file_?name)"/i.test(text)) return;
  try {
    walk(JSON.parse(text), 0, scope);
  } catch {
    for (const match of text.matchAll(/"([A-Za-z_-]{1,40})"\s*:\s*(?:"((?:[^"\\]|\\.)*)"|(-?\d{5,18})(?![\d.]))/g)) learnPair(match[1], match[2] ?? match[3], scope);
  }
}

const WORD_START = /(?<![\p{L}\p{N}])[\p{L}\p{N}]/gu;

/** Text as `outerHTML` gives it: `& < >` escaped in content, `& "` in attributes. */
function variants(text: string): string[] {
  const amp = text.replace(/&/g, '&amp;');
  return [...new Set([text, amp.replace(/</g, '&lt;').replace(/>/g, '&gt;'), amp.replace(/"/g, '&quot;')])];
}

function index(): Set<string> {
  if (starts) return starts;
  starts = new Set();
  const all: string[] = [];
  for (const text of texts) {
    for (const one of variants(text)) {
      all.push(one);
      for (const match of one.matchAll(WORD_START)) {
        const piece = one.slice(match.index, match.index + MIN_FRAGMENT);
        if (piece.length === MIN_FRAGMENT) starts.add(piece);
      }
    }
  }
  joined = all.join('\u0000');
  return starts;
}

export interface KnownRange {
  start: number;
  end: number;
  kind: 'text' | 'name' | 'number';
}

function stretch(input: string, from: number, to: number): [number, number] {
  const at = joined.indexOf(input.slice(from, to));
  if (at < 0) return [from, to];
  let start = from;
  let back = at;
  while (start > 0 && back > 0 && joined[back - 1] !== '\u0000' && joined[back - 1] === input[start - 1]) {
    start -= 1;
    back -= 1;
  }
  let end = to;
  let ahead = at + (to - from);
  while (end < input.length && ahead < joined.length && joined[ahead] !== '\u0000' && joined[ahead] === input[end]) {
    end += 1;
    ahead += 1;
  }
  return [start, end];
}

function merge(ranges: KnownRange[]): KnownRange[] {
  ranges.sort((a, b) => a.start - b.start);
  const out: KnownRange[] = [];
  for (const range of ranges) {
    const last = out[out.length - 1];
    if (last && range.start <= last.end && last.kind === range.kind) last.end = Math.max(last.end, range.end);
    else if (!last || range.start >= last.end) out.push({ ...range });
  }
  return out;
}

export function knownRanges(input: string): KnownRange[] {
  const ranges: KnownRange[] = [];
  if (texts.size > 0 && input.length >= MIN_QUOTED) {
    const pieces = index();
    let runEnd = -1;
    for (const match of input.matchAll(WORD_START)) {
      const at = match.index;
      if (at < runEnd) continue;
      const piece = input.slice(at, at + MIN_FRAGMENT);
      if (piece.length < MIN_FRAGMENT || !pieces.has(piece)) continue;
      const [start, end] = stretch(input, at, at + MIN_FRAGMENT);
      ranges.push({ start, end, kind: 'text' });
      runEnd = end;
    }
    for (const match of input.matchAll(/«([^«»]{8,600})»/g)) {
      const inner = match[1].trim();
      if (inner.length >= MIN_QUOTED && joined.includes(inner)) {
        const start = match.index + 1 + match[1].indexOf(inner);
        ranges.push({ start, end: start + inner.length, kind: 'text' });
      }
    }
  }
  const textRanges = merge(ranges);
  if (names.size === 0 && numbers.size === 0) return textRanges;
  const all = [...textRanges];
  const free = (start: number, end: number) => !all.some((range) => start < range.end && end > range.start);
  for (const range of nameRanges(input)) if (free(range.start, range.end)) all.push(range);
  for (const range of numberRanges(input)) if (free(range.start, range.end)) all.push(range);
  return all.sort((a, b) => a.start - b.start);
}

const WORD_END = /[\p{L}\p{N}_]/u;

/** Known names, word by word (Мс8): a lookup per word, however many people the dictionary holds. Case-sensitive. */
function nameRanges(input: string): KnownRange[] {
  const out: KnownRange[] = [];
  if (names.size === 0) return out;
  let until = 0;
  for (const match of input.matchAll(WORD)) {
    const word = match[0];
    const start = match.index;
    if (start < until) continue;
    const whole = phrases.get(word)?.find((phrase) => input.startsWith(phrase, start) && !WORD_END.test(input[start + phrase.length] ?? ' '));
    if (whole) {
      out.push({ start, end: start + whole.length, kind: 'name' });
      until = start + whole.length;
    } else if (names.has(word)) out.push({ start, end: start + word.length, kind: 'name' });
    else if (/['’.-]/.test(word)) {
      let at = start;
      for (const part of word.split(/(['’.-])/)) {
        if (names.has(part)) out.push({ start: at, end: at + part.length, kind: 'name' });
        at += part.length;
      }
    }
  }
  return out;
}

const NUMBER_LIKE = /\+?\d(?:[\d\s().-]*\d)?/g;

const isKnownNumber = (digits: string): boolean => numbers.has(digits) || (digits.length > 10 && numbers.has(digits.slice(-10)));

/** Known Telegram IDs and phones in any writing: "+7 (999) 123-45-67", "89991234567", "123456789". */
function numberRanges(input: string): KnownRange[] {
  const out: KnownRange[] = [];
  if (numbers.size === 0) return out;
  for (const match of input.matchAll(NUMBER_LIKE)) {
    const whole = match[0];
    const start = match.index;
    if (isKnownNumber(whole.replace(/\D/g, ''))) {
      out.push({ start, end: start + whole.length, kind: 'number' });
      continue;
    }
    // "ID 123456789 42": a run of spaces joined two numbers — each digit run is looked at on its own.
    for (const run of whole.matchAll(/\d+/g)) {
      if (run[0].length >= MIN_DIGITS && isKnownNumber(run[0])) out.push({ start: start + run.index, end: start + run.index + run[0].length, kind: 'number' });
    }
  }
  return out;
}

function replaceRanges(input: string, fn: (piece: string, kind: KnownRange['kind']) => string): string {
  const ranges = knownRanges(input);
  if (ranges.length === 0) return input;
  let out = '';
  let pos = 0;
  for (const range of ranges) {
    out += input.slice(pos, range.start) + fn(input.slice(range.start, range.end), range.kind);
    pos = range.end;
  }
  return out + input.slice(pos);
}

export function maskKnown(input: string): string {
  return replaceRanges(input, (piece, kind) => (kind === 'name' ? '[имя]' : kind === 'number' ? '[номер]' : hiddenText(piece)));
}

export function installKnownMask(): void {
  setKnownMask(maskKnown);
}

export function resetKnown(): void {
  changed();
  names.clear();
  texts.clear();
  numbers.clear();
  phrases.clear();
  starts = null;
  joined = '';
}
